import {
	Button,
	Dropdown,
	Spin,
	Table,
	TableProps,
	Tooltip,
	Upload,
	Typography,
	Checkbox,
	Modal,
	Input,
	Progress,
} from "antd";
import { GetAntIcon } from "../utils/ant_icons";
import { createWelldocsApi } from "../services/api-server/welldocsApi";
import {
	ALL_SHAREPOINT_ACTIONS,
	SharepointAction,
	SharepointApi,
	SharepointListing,
	SharepointOwnerRow,
} from "../types/sharepointApi";
import { useContext, useEffect, useMemo, useState } from "react";
import { socket } from "../utils/socket";
import Emitter from "../utils/emitter";
import { HomeContext } from "../containers/Home";
import { capitalizeFirstLetter } from "../utils/string_convert_helper";
import elementalLoadingLogo from "../assets/image/elemental 'e'.svg";
import elementalLoadingOutline from "../assets/image/outline 'e'.svg";
import SearchButton from "./SearchButton";
import { renderTableCheckboxFilter } from "./Table/TableCheckboxFilter";
import fileTools from "../utils/fileTools";
import dayjs from "dayjs";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { useForm } from "antd/es/form/Form";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import SharepointSearch from "./SharepointSearch";
import SharepointQuickAccess from "./SharepointQuickAccess";
import { getFileIcon } from "../utils/fileIcons";
import { getSharePointErrorMessage } from "../utils/sharepointErrors";
import { folderLink, parseFolderParam } from "../utils/sharepointLinks";

const { Text } = Typography;
const illegalFilenameRegex =
	/^(?!.*[\\\/:*?"<>|%])(?!\s)(?!.*\s$)(?!.*\.$)(?!^\s*$).+$/;
interface BaseProps {
	visible: boolean;
	setDisabled: (disabled: boolean) => void;
	id: string;
	name: string;
	type: "well" | "operation" | "project" | "well-examination";
	rest?: any;
	/** Backend adapter. Defaults to the legacy sharepoint module; required for "well-examination". */
	api?: SharepointApi;
	/** Actions the current user may perform; everything else is hidden. */
	allowedActions?: ReadonlySet<SharepointAction>;
}

const QUICK_ACCESS_LIMIT = 10;

const toQuickAccessFiles = (owners: SharepointOwnerRow[]) =>
	owners
		.filter((item) => item.item_type === "file")
		.sort(
			(a, b) =>
				new Date(b.createdAt || 0).getTime() -
				new Date(a.createdAt || 0).getTime()
		)
		.slice(0, QUICK_ACCESS_LIMIT);

const SharepointComponent = ({
	visible,
	setDisabled,
	type,
	id,
	name,
	rest,
	api,
	allowedActions = ALL_SHAREPOINT_ACTIONS,
}: BaseProps) => {
	const context: any = useContext(HomeContext);
	const location = useLocation();

	const resolvedApi = useMemo<SharepointApi>(() => {
		if (api) return api;
		if (type === "well-examination") {
			throw new Error(
				'SharepointComponent requires an `api` for type "well-examination"'
			);
		}
		return createWelldocsApi({
			tenantKey: context.currentTenantKey,
			keyId: id,
			name,
			type,
			user: context.userInfo?.user,
			tenantContainerId: context.currentTenantData?.container_id,
			operationCode: rest?.operation_code,
		});
	}, [
		api,
		type,
		id,
		name,
		rest?.operation_code,
		context.currentTenantKey,
		context.currentTenantData?.container_id,
		context.userInfo?.user,
	]);

	const can = (action: SharepointAction) => allowedActions.has(action);

	const navigate = useNavigate();

	const [formRef] = useForm();

	const [checkDone, setCheckDone] = useState<boolean>(false);

	const [sharepointDisabled, setSharepointDisabled] = useState<boolean>(false);

	//document search
	const [searchRecord, setSearchRecord] = useState<any>([]);
	const [filteredItems, setFilteredItems] = useState<any>([]);
	const [searchCacheKey, setSearchCacheKey] = useState(0);

	//Folder Modal
	const [folderModalOpen, setFolderModalOpen] = useState<boolean>(false);
	const [folderName, setFolderName] = useState<string>("");
	//File Modal
	// const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
	const [fileList, setFileList] = useState<any>([]);
	const [uploadLength, setUploadLength] = useState<any>(0);
	//Rename Modal
	const [renameModalOpen, setRenameModalOpen] = useState<boolean>(false);
	const [tempFileName, setTempFileName] = useState<string>("");
	const [newFileName, setNewFileName] = useState<string>("");
	const [tempRecord, setTempRecord] = useState<any>({});
	//Confirmation Modal
	const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
	const [isDeleteMultiple, setIsDeleteMultiple] = useState<boolean>(false);

	const [confirmUploadModalOpen, setConfirmUploadModalOpen] =
		useState<boolean>(false);

	//Loading Modal
	const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
	const [actionType, setActionType] = useState<string>("");
	const [loadingPercentage, setLoadingPercentage] = useState<number>(0);
	const [rootFolderId, setRootFolderId] = useState<any>(null);
	const [currentFolderFiles, setCurrentFolderFiles] = useState<any>([]);
	const [currentFolderId, setCurrentFolderId] = useState<any>(null);
	const [containerId, setCurrentContainerId] = useState<any>(null);

	const [ownerList, setOwnerList] = useState<any>([]);
	const [quickAccessFiles, setQuickAccessFiles] = useState<any>([]);
	const [quickAccessFileMetaData, setQuickAccessFileMetaData] = useState<any>(
		{}
	);
	const [folderHierarchy, setFolderHierarchy] = useState<any>([]);
	const [quickAccessCollapsed, setQuickAccessCollapsed] =
		useState<boolean>(false);
	const [hovered, setHovered] = useState<any>(null);
	const [rowSelectedIDs, setRowSelectedIDs] = useState<any>([]);
	const [selectionType, setSelectionType] = useState<"checkbox" | "radio">(
		"checkbox"
	);

	const [draggingRecord, setDraggingRecord] = useState<any>(null);

	const removeFilesWithoutOwner = (folderItems: any, ownerList: any) => {
		// from each folder item, find the owner
		folderItems.forEach((item: any) => {
			let owner = ownerList.find((owner: any) => item.id === owner.item_id);
			item.owner = owner?.owner_email || null;
		});

		// remove folder if owner does not exist
		folderItems = folderItems.filter((item: any) => item.owner);
		setCurrentFolderFiles(folderItems);
		setFilteredItems(folderItems);
	};

	/** Apply a listing and its owner rows (fetched separately when the backend does not include them). */
	const applyListing = async (listing: SharepointListing) => {
		const owners = listing.owners ?? (await resolvedApi.getOwners());
		setQuickAccessFiles(toQuickAccessFiles(owners));
		setOwnerList(owners);
		removeFilesWithoutOwner(listing.items || [], owners);
	};

	const refreshCurrentFolder = async () => {
		const listing = await resolvedApi.listItems(currentFolderId, containerId);
		await applyListing(listing);
		setSearchCacheKey((k) => k + 1);
	};

	useEffect(() => {
		if (context.currentTenantKey && context.allData && id) {
			const loadRoot = async () => {
				try {
					const root = await resolvedApi.getRoot();
					const listing = await resolvedApi.listItems(
						root.folderId,
						root.containerId
					);
					const folderId = root.folderId || listing.folderId;
					const resolvedContainerId = root.containerId || listing.containerId;
					if (setDisabled) {
						setDisabled(false);
					}
					setRootFolderId(folderId);
					setCurrentFolderId(folderId);
					setCurrentContainerId(resolvedContainerId);
					await applyListing(listing);
				} catch (error) {
					setSharepointDisabled(true);
					if (setDisabled) {
						setDisabled(true);
					}
					Emitter.emit("alert", {
						type: "error",
						message:
							"An unexpected error has occurred for documents, please contact administrator.",
						timeout: 5000,
						top: true,
					});
					console.log(error);
				}
			};
			loadRoot();
		}
		socket.on("ACTION_PROGRESS", (payload: any) => {
			setLoadingPercentage(payload);
		});

		Emitter.on("ACTION_PROGRESS", (percentage: any) => {
			setLoadingPercentage(percentage);
		});

		return () => {
			socket.removeListener("ACTION_PROGRESS");
			Emitter.remove("ACTION_PROGRESS");
		};
	}, [context.currentTenantKey, context.allData, id, resolvedApi]);

	useEffect(() => {
		if (containerId) {
			// Fetch the images for each file
			const fetchImages = async () => {
				const fileMetaData: any = {};

				await Promise.all(
					quickAccessFiles.map(async (file: any) => {
						try {
							// Call your API to get the image URL for each file
							const thumbnailResponse: any = await resolvedApi.getThumbnail(
								file.item_id,
								containerId
							);
							const fileResponse: any = await resolvedApi.getFile(
								file.item_id,
								containerId
							);
							fileMetaData[file.item_id] = {
								name: fileResponse?.name,
								filetype: fileResponse.file?.mimeType || "undefined",
								thumbnail_url: thumbnailResponse.value[0]?.medium?.url,
								createdDateTime: fileResponse.createdDateTime,
							};
							// Assuming `data.imageUrl` contains the image URL
						} catch (error: any) {
							console.error(
								`Failed to fetch image for file ${file.item_id}`,
								error
							);
							fileMetaData[file.item_id] = null; // Set a fallback image in case of error
						}
					})
				);

				setQuickAccessFileMetaData(fileMetaData);
			};

			fetchImages();
		}
	}, [quickAccessFiles, containerId]);

	useEffect(() => {
		const hierarchyMaker = async (folderParam: string[]) => {
			let hierarchy: any = [];
			for (const [index, folder] of folderParam.entries()) {
				console.log("hierarchy", hierarchy);
				let response: any = await resolvedApi.getFile(folder, containerId);
				hierarchy.push({ id: folder, name: response.name });
				if (index === folderParam.length - 1) {
					handleFolderChange(folder, response.name, null, false);
					setFolderHierarchy(hierarchy);
				}
			}
		};
		if (containerId && ownerList.length > 0 && !checkDone) {
			const folderParam = parseFolderParam(location.search);
			if (folderParam?.length) {
				hierarchyMaker(folderParam);
			}

			setCheckDone(true);
		}
	}, [containerId, ownerList]);

	// Handle file uploads
	useEffect(() => {
		if (
			fileList.length > 0 &&
			uploadLength > 0 &&
			fileList.length === uploadLength
		) {
			//Check for filename duplicates
			let filenames = fileList.map((file: any) => file.name);
			if (filenames) {
				console.log(filenames, currentFolderFiles);
				if (
					currentFolderFiles.some((file: any) => filenames.includes(file.name))
				) {
					setConfirmUploadModalOpen(true);
				} else {
					handleFileUpload(fileList);
				}
			}
		}
	}, [uploadLength, fileList]);

	const handleFolderChange = (
		folderID: any,
		folderName: string,
		index: any = null,
		setHierarchy: boolean = true
	) => {
		Emitter.emit("loading", {
			name: "sharepoint-component",
			isLoading: true,
		});
		resolvedApi
			.listItems(folderID, containerId)
			.then((response) => {
				if (index == null) {
					if (setHierarchy) {
						setFolderHierarchy([
							...folderHierarchy,
							{ id: folderID, name: folderName },
						]);
					}
				} else if (index == -1) {
					if (setHierarchy) {
						setFolderHierarchy([]);
					}
				} else {
					let array = [...folderHierarchy].slice(0, index + 1);
					if (setHierarchy) {
						setFolderHierarchy(array);
					}
				}

				setRowSelectedIDs([]);
				setSearchRecord([]);

				const owners = response.owners ?? ownerList;
				if (response.owners) {
					setOwnerList(response.owners);
					setQuickAccessFiles(toQuickAccessFiles(response.owners));
				}
				removeFilesWithoutOwner(response.items || [], owners);
				setCurrentFolderId(folderID);
			})
			.catch((error: any) => {
				console.log(error);
				Emitter.emit("alert", {
					type: "error",
					message: "Folder contents could not be loaded",
					description: getSharePointErrorMessage(error),
					top: true,
					closeable: false,
					timeout: 5000,
				});
			})
			.finally(() => {
				Emitter.emit("loading", {
					name: "sharepoint-component",
					isLoading: false,
				});
			});
	};

	const handleBeforeUpload = (file: any) => {
		// Prevent the file from being uploaded immediately
		setFileList((prevList: any) => [...prevList, file]);
		return false; // This stops the upload process
	};

	const handleRemove = (file: any) => {
		setFileList((prevList: any) =>
			prevList.filter((item: any) => item.uid !== file.uid)
		);
	};

	const handleSearch = (value: any) => {
		console.log(value);

		let record: any = [...searchRecord];
		let recordIndex = record.findIndex((item: any) => {
			return item.columnKey === value.columnKey;
		});
		if (recordIndex !== -1) {
			if (value.value != "") {
				record[recordIndex] = value;
				setSearchRecord(record);
			} else {
				record.splice(recordIndex, 1);
				setSearchRecord(record);
			}
		} else {
			if (value.value != "") {
				record.push(value);
				setSearchRecord(record);
			}
		}

		//Filter data and table columns based on search record
		let data: any = [...currentFolderFiles];
		let filteredData = data
			?.map((element: any) => {
				let bool = record.every((item: any) => {
					if (Array.isArray(element[item.columnKey])) {
						return element[item.columnKey].some((arrayItem: any) =>
							arrayItem.toLowerCase().includes(item.value.toLowerCase())
						);
					} else {
						return element[item.columnKey]
							?.toLowerCase()
							.includes(item.value.toLowerCase());
					}
				});
				if (bool) return element;
			})
			.filter((element: any) => element !== null && element !== undefined);
		setFilteredItems(filteredData);
		data = filteredData;
	};

	const handleFolderCreation = () => {
		let new_name = folderName;
		// check if the folder name is valid
		// regex test, cannot include special characters and white spaces, a space is allowed
		if (!illegalFilenameRegex.test(new_name)) {
			Emitter.emit("alert", {
				type: "error",
				message: `Invalid name. Only letters, numbers, spaces, and the characters (), _, and - are allowed. White spaces are not allowed. Changes were not saved.`,
				top: true,
				closeable: false,
				timeout: 3000,
				description: "",
			});
		} else if (
			currentFolderFiles?.some(
				(file: any) => file.name.toLowerCase() === new_name.toLowerCase()
			)
		) {
			Emitter.emit("alert", {
				type: "error",
				message: `The folder name already exists. Ensure the name is unique. Changes have not been saved.`,
				description: "",
				top: true,
				closeable: false,
				timeout: 3000,
			});
		} else {
			const folderHierarchyIds = folderHierarchy.map(
				(folder: any) => folder.id
			);
			Emitter.emit("loading", {
				name: "sharepoint-component",
				isLoading: true,
			});
			resolvedApi
				.createFolder(
					new_name,
					currentFolderId,
					containerId,
					folderHierarchyIds
				)
				.then(() => refreshCurrentFolder())
				.then(() => {
					setFolderModalOpen(false);
					setFolderName("");
					Emitter.emit("alert", {
						type: "success",
						message: `Folder has been successfully created`,
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});
				})
				.catch((error: any) => {
					const code = error?.error?.code || error?.code;
					const nameExists = code === "nameAlreadyExists";
					Emitter.emit("alert", {
						type: "error",
						message: nameExists
							? `The folder name already exists. Ensure the name is unique. Changes have not been saved.`
							: `Folder could not be created`,
						description: nameExists
							? ""
							: getSharePointErrorMessage(error, new_name),
						top: true,
						closeable: false,
						timeout: 3000,
					});
				})
				.finally(() => {
					Emitter.emit("loading", {
						name: "sharepoint-component",
						isLoading: false,
					});
				});
		}
	};

	const handleFileUpload = (fileList: any, folderId: any = currentFolderId) => {
		setActionType("upload");
		setLoadingPercentage(0);
		setLoadingModalOpen(true);
		let folderHierarchyIds = folderHierarchy.map((folder: any) => folder.id);

		// for each file, check file name
		for (const file of fileList) {
			if (!illegalFilenameRegex.test(file.name)) {
				Emitter.emit("alert", {
					type: "error",
					message: `Invalid name. Only letters, numbers, spaces, and the characters (), _, and - are allowed. White spaces are not allowed. Files were not uploaded.`,
					top: true,
					closeable: false,
					timeout: 3000,
					description: "",
				});
				setLoadingModalOpen(false);
				setFileList([]);

				return false;
			}
		}
		resolvedApi
			.uploadFiles(
				fileList,
				folderId,
				containerId,
				folderHierarchyIds,
				socket.id
			)
			.then(async (response: any) => {
				setFileList([]);
				setLoadingModalOpen(false);
				const failedUploads = response?.failed_uploads || [];

				await refreshCurrentFolder();

				if (failedUploads.length > 0) {
					const messages = failedUploads.map((failure: any) =>
						getSharePointErrorMessage(failure?.error, failure?.file_name)
					);
					Emitter.emit("alert", {
						type: "warning",
						message: `${failedUploads.length} file(s) could not be uploaded`,
						description: messages.join("\n"),
						top: true,
						closeable: false,
						timeout: 5000,
					});
				} else {
					Emitter.emit("alert", {
						type: "success",
						message: `${fileList.length} file(s) have been successfully uploaded`,
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});
				}
			})
			.catch((error: any) => {
				setFileList([]);
				setLoadingModalOpen(false);
				const message = getSharePointErrorMessage(error);
				Emitter.emit("alert", {
					type: "error",
					message: `${fileList.length} file(s) could not be uploaded`,
					description: message,
					top: true,
					closeable: false,
					timeout: 5000,
				});
			});
	};

	const handleFileRename = () => {
		try {
			// check if the folder name is valid
			// regex test, cannot include special characters and white spaces, a space is allowed

			let new_name = newFileName + tempRecord?.fileSuffix;
			let count = 1;

			if (!illegalFilenameRegex.test(newFileName)) {
				Emitter.emit("alert", {
					type: "error",
					message: `Invalid name. Only letters, numbers, spaces, and the characters (), _, and - are allowed. White spaces are not allowed. Changes were not saved.`,
					top: true,
					closeable: false,
					timeout: 3000,
					description: "",
				});
			} else if (
				tempRecord.folder &&
				currentFolderFiles?.some(
					(file: any) =>
						file.name.toLowerCase() === new_name.toLowerCase() &&
						file.id != tempRecord.id &&
						file.folder
				)
			) {
				Emitter.emit("alert", {
					type: "error",
					message: `The folder name already exists. Ensure the name is unique. Changes have not been saved.`,
					description: "",
					top: true,
					closeable: false,
					timeout: 3000,
				});
			} else {
				Emitter.emit("loading", {
					name: "sharepoint-component",
					isLoading: true,
				});

				if (tempRecord.file) {
					while (
						currentFolderFiles?.some(
							(file: any) =>
								file.name.toLowerCase() === new_name.toLowerCase() &&
								file.id != tempRecord.id &&
								file.file
						)
					) {
						const lastDotIndex = new_name.lastIndexOf(".");
						if (lastDotIndex !== -1) {
							new_name =
								`${new_name.substring(0, lastDotIndex)} (${count})` +
								tempRecord?.fileSuffix;
						} else {
							new_name = newFileName + ` (${count})` + tempRecord?.fileSuffix;
						}
						count++;
					}
				}

				resolvedApi
					.renameItem(tempRecord.id, containerId, new_name)
					.then((response: any) => {
						let foundRootIndex = currentFolderFiles.findIndex(
							(record: any) => record.id === response.id
						);
						let foundFilteredIndex = filteredItems.findIndex(
							(record: any) => record.id === response.id
						);
						if (foundRootIndex != -1) {
							let _newFolderFiles = [...currentFolderFiles];
							_newFolderFiles[foundRootIndex] = response;
							setCurrentFolderFiles(_newFolderFiles || []);
						}
						if (foundFilteredIndex != -1) {
							let _newFilteredFiles = [...filteredItems];
							_newFilteredFiles[foundFilteredIndex] = response;
							setFilteredItems(_newFilteredFiles || []);
						}

						setRenameModalOpen(false);
						setTempFileName("");
						setNewFileName("");
						setTempRecord({});
						Emitter.emit("alert", {
							type: "success",
							message: `The ${
								tempRecord.file ? "file" : "folder"
							} has been successfully renamed`,
							description: "",
							top: true,
							closeable: false,
							timeout: 3000,
						});
						Emitter.emit("loading", {
							name: "sharepoint-component",
							isLoading: false,
						});
					})
					.catch((error: any) => {
						const errorCode = error?.error?.code || error?.code;
						Emitter.emit("loading", {
							name: "sharepoint-component",
							isLoading: false,
						});
						if (errorCode === "nameAlreadyExists") {
							Emitter.emit("alert", {
								type: "error",
								message: `The name has already exist, please try another.`,
								description: "",
								top: true,
								closeable: false,
								timeout: 3000,
							});
						} else {
							const message = getSharePointErrorMessage(error, tempRecord.name);
							Emitter.emit("alert", {
								type: "error",
								message: `The ${tempRecord.file ? "file" : "folder"} could not be renamed`,
								description: message,
								top: true,
								closeable: false,
								timeout: 5000,
							});
						}
					});
			}
		} catch (error: any) {
			setRenameModalOpen(false);
			setTempFileName("");
			setNewFileName("");
			setTempRecord({});
			Emitter.emit("loading", {
				name: "sharepoint-component",
				isLoading: false,
			});
		}
	};

	const handleFileDelete = (
		idList: any,
		multiple: boolean,
		record: any = null,
		filetype: any = null,
		index: any = -1
	) => {
		setDeleteModalOpen(false);
		setLoadingModalOpen(true);
		setActionType("delete");
		setLoadingPercentage(0);
		resolvedApi
			.deleteItems(idList, containerId, socket.id)
			.then(() => {
				if (multiple) {
					let tempFolderFiles = [...filteredItems];
					let _newFolderFiles = [...currentFolderFiles];
					let tempQuickAccessFiles = [...quickAccessFiles];

					for (const fileID of rowSelectedIDs) {
						let foundRootIndex = _newFolderFiles.findIndex(
							(record: any) => record.id === fileID
						);

						let foundFilteredIndex = tempFolderFiles.findIndex(
							(record: any) => record.id === fileID
						);
						if (foundRootIndex != -1) {
							_newFolderFiles.splice(foundRootIndex, 1);
						}
						if (foundFilteredIndex != -1) {
							tempFolderFiles.splice(foundFilteredIndex, 1);
						}
					}

					setCurrentFolderFiles(_newFolderFiles || []);
					setFilteredItems(tempFolderFiles);
					setRowSelectedIDs([]);
				} else {
					let foundRootIndex = currentFolderFiles.findIndex(
						(item: any) => item.id === record?.id
					);
					if (foundRootIndex != -1) {
						let _newFolderFiles = [...currentFolderFiles];
						_newFolderFiles.splice(foundRootIndex, 1);
						setCurrentFolderFiles(_newFolderFiles || []);
					}

					let tempFolderFiles = [...filteredItems];
					tempFolderFiles.splice(index, 1);

					setFilteredItems(tempFolderFiles);
					setRowSelectedIDs([]);
				}

				setLoadingModalOpen(false);
				refreshCurrentFolder().catch((error: any) => console.log(error));

				Emitter.emit("alert", {
					type: "success",
					message: `${
						multiple
							? `${rowSelectedIDs.length} checked item(s) have`
							: `${capitalizeFirstLetter(filetype)} has`
					} been successfully deleted`,
					description: "",
					top: true,
					closeable: false,
					timeout: 3000,
				});
			})
			.catch((error: any) => {
				setDeleteModalOpen(false);
				setLoadingModalOpen(false);

				const failedItems = error?.failed_items || [];
				let description = "";
				if (failedItems.length > 0) {
					const messages = failedItems.map((item: any) => {
						const record = [...currentFolderFiles, ...filteredItems].find(
							(file: any) => file.id === item.item_id
						);
						const name = record?.name || item.item_id;
						return getSharePointErrorMessage(item?.error, name);
					});
					description = messages.join("\n");
				} else {
					const targetName = multiple ? undefined : tempRecord?.name;
					description = getSharePointErrorMessage(error, targetName);
				}

				Emitter.emit("alert", {
					type: "error",
					message: `${
						multiple
							? `${rowSelectedIDs.length} checked item(s)`
							: `${capitalizeFirstLetter(filetype)}`
					} could not be deleted`,
					description,
					top: true,
					closeable: false,
					timeout: 5000,
				});
			});
	};

	const handleFileMove = (recordId: any, draggedRecordId: any) => {
		let recordsToMove = [draggedRecordId];

		if (rowSelectedIDs.includes(draggedRecordId)) {
			recordsToMove = rowSelectedIDs;
		}
		if (draggedRecordId === recordId) {
			return;
		}
		setLoadingModalOpen(true);
		setActionType("move");
		setLoadingPercentage(0);

		resolvedApi
			.moveItems(recordsToMove, recordId, containerId)
			.then((response: any) => {
				// remove file from file list
				let _currentFolderFiles = currentFolderFiles.filter(
					(file: any) => !recordsToMove.includes(file.id)
				);
				let _filteredItems = filteredItems.filter(
					(file: any) => !recordsToMove.includes(file.id)
				);
				setCurrentFolderFiles(_currentFolderFiles);
				setFilteredItems(_filteredItems);
				setLoadingModalOpen(false);
				Emitter.emit("alert", {
					type: "success",
					message: "File(s) moved successfully",
					description: "",
					top: true,
					closeable: false,
					timeout: 3000,
				});
			})
			.catch((error: any) => {
				setLoadingModalOpen(false);
				const message = getSharePointErrorMessage(error);
				Emitter.emit("alert", {
					type: "error",
					message: "File(s) could not be moved",
					description: message,
					top: true,
					closeable: false,
					timeout: 5000,
				});
			});
	};

	const folderMemo = useMemo(() => {
		const copyLink = (path: string) => {
			navigator.clipboard.writeText(`${window.location.origin}${path}`);
			Emitter.emit("alert", {
				type: "success",
				message: "Link copied to clipboard!",
				description: "",
				top: true,
				closeable: false,
				timeout: 3000,
			});
		};

		const openRename = (record: any) => {
			let tempRecord = { ...record };
			if (record.file) {
				const lastDotIndex = record.name?.lastIndexOf(".");
				const prefix = record.name.substring(0, lastDotIndex);
				tempRecord.fileSuffix = "." + record.name.substring(lastDotIndex + 1);
				setTempFileName(prefix);
				setNewFileName(prefix);
			} else {
				tempRecord.fileSuffix = "";
				setTempFileName(record.name);
				setNewFileName(record.name);
			}
			setTempRecord(tempRecord);
			setRenameModalOpen(true);
		};

		const openDelete = (record: any, index: number) => {
			let tempRecord = { ...record };
			tempRecord.index = index;
			setIsDeleteMultiple(false);
			setDeleteModalOpen(true);
			setTempRecord(tempRecord);
		};

		const dropdownItemOptions: any = (
			fileType: string,
			record: any,
			index: number
		) => {
			const renameItem = can("rename") && {
				label: "Rename",
				key: "-1",
				onClick: () => openRename(record),
			};
			const deleteItems = can("delete")
				? [
						{ type: "divider" },
						{
							label: "Delete",
							key: "4",
							onClick: () => openDelete(record, index),
						},
					]
				: [];

			if (fileType == "folder") {
				const link = folderLink(location.pathname, [
					...folderHierarchy.map((folder: any) => folder.id),
					record.id,
				]);
				return [
					renameItem,
					{
						label: "Copy link",
						key: "2",
						onClick: () => copyLink(link),
					},
					...deleteItems,
				].filter(Boolean);
			} else if (fileType == "file") {
				const previewPath = resolvedApi.previewPath(
					record.id,
					containerId,
					location.pathname
				);
				return [
					renameItem,
					can("preview") && {
						label: "Preview",
						key: "0",
						onClick: () => window.open(previewPath, "_blank"),
					},
					can("collab") &&
						fileTools.checkSharepointEdittable(record.file.mimeType) && {
							label: "Edit",
							key: "1",
							onClick: () => {
								resolvedApi
									.collaborate(record.id, containerId)
									.then((response: any) => {
										window.open(response?.link.webUrl, "__blank");
									});
							},
						},
					{
						label: "Copy link",
						key: "2",
						onClick: () => copyLink(previewPath),
					},
					can("download") && {
						label: "Download",
						key: "3",
						onClick: () => {
							resolvedApi
								.getDownloadUrl(record, containerId)
								.then((downloadUrl) => {
									if (downloadUrl) {
										window.open(downloadUrl, "_self");
									}
								})
								.catch((error: any) => {
									Emitter.emit("alert", {
										type: "error",
										message: "File could not be downloaded",
										description: getSharePointErrorMessage(error, record.name),
										top: true,
										closeable: false,
										timeout: 5000,
									});
								});
						},
					},
					...deleteItems,
				].filter(Boolean);
			} else {
				return [];
			}
		};

		const filterFileTypeOptions: any = () => {
			let filteredData = [...filteredItems];
			let arrayTypes: any = filteredData.map((record: any) => {
				if (record.folder) {
					return "File folder";
				} else if (record.file) {
					return record.file.mimeType;
				}
			});
			const uniqueArray = arrayTypes.filter(
				(item: any, index: any) => arrayTypes.indexOf(item) === index
			);
			return uniqueArray.map((element: any) => ({
				text: fileTools.getReadableFileType(element) || element,
				value: element,
			}));
		};

		const breadcrumbRenderer = () => {
			if (folderHierarchy.length > 0) {
				return (
					<>
						<a
							className="document-breadcrumb-clickable-text"
							onClick={() => {
								handleFolderChange(rootFolderId, "", -1);
							}}
							onDrop={(event: any) => {
								event.preventDefault();
								event.stopPropagation();
								console.log("Record to be dropped: ", event);
								const recordId = event.dataTransfer.getData("recordId");
								if (can("move")) handleFileMove(rootFolderId, recordId);
							}}
							onDragOver={(event: any) => {
								event.preventDefault();
							}}
						>
							Contents
						</a>
						{folderHierarchy.map((folder: any, index: number) => {
							if (index === folderHierarchy.length - 1) {
								return (
									<>
										<span className="document-breadcrumb-seperator">/</span>
										<span className="document-breadcrumb-text">
											{folder.name || "undefined"}
										</span>
									</>
								);
							} else {
								return (
									<>
										<span className="document-breadcrumb-seperator">/</span>
										<a
											className="document-breadcrumb-clickable-text"
											onClick={() => {
												handleFolderChange(folder.id, folder.name, index);
											}}
											onDrop={(event: any) => {
												event.preventDefault();
												event.stopPropagation();
												const recordId = event.dataTransfer.getData("recordId");
												if (can("move")) handleFileMove(folder.id, recordId);
											}}
											onDragOver={(event: any) => {
												event.preventDefault();
											}}
										>
											{folder.name || "undefined"}
										</a>
									</>
								);
							}
						})}
					</>
				);
			} else {
				return <span>Contents</span>;
			}
		};

		return (
			<>
				{" "}
				<div className="document-container-header">
					<div id={"documentBreadcrumb"} className="document-breadcrumb">
						{breadcrumbRenderer()}
					</div>
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							marginLeft: "auto",
							gap: "10px",
						}}
					>
						<SharepointSearch
							api={resolvedApi}
							tenantKey={context.currentTenantKey}
							containerId={containerId}
							rootFolderId={rootFolderId}
							keyId={id}
							placeholder="Search files"
						/>
						<Button
							disabled={sharepointDisabled}
							icon={GetAntIcon("delete")}
							style={
								rowSelectedIDs.length > 0 && can("delete")
									? {}
									: { display: "none" }
							}
							onClick={() => {
								setDeleteModalOpen(true);
								setIsDeleteMultiple(true);
							}}
						>
							Delete
						</Button>
						{can("createFolder") && (
							<Button
								disabled={sharepointDisabled}
								icon={GetAntIcon("folder-outline")}
								onClick={() => {
									setFolderModalOpen(true);
								}}
							>
								Create folder
							</Button>
						)}
						{can("upload") && (
							<Upload
								disabled={sharepointDisabled}
								multiple
								beforeUpload={() => false}
								fileList={fileList}
								onRemove={handleRemove}
								onChange={(info: any) => {
									setUploadLength(info.fileList.length);
									setFileList((prevList: any) => [...prevList, info.file]);
								}}
								itemRender={() => <></>}
							>
								<Button icon={GetAntIcon("upload")}>Upload file</Button>
							</Upload>
						)}
					</div>
				</div>
				<Table
					// rowSelection={{
					// 	type: selectionType,
					// 	...rowSelection,
					// 	hideSelectAll: true,
					// }}
					// rowKey={"id"}

					rowHoverable={true}
					footer={undefined}
					pagination={false}
					style={{ width: "100%" }}
					rootClassName="audit-log-table" //styling only
					className="documents-table"
					bordered
					dataSource={filteredItems}
					// scroll={tableHeight && { y: tableHeight, x: 0 }}
					tableLayout="auto"
					columns={[
						{
							title: (
								<SearchButton
									handleSearch={handleSearch}
									headerName="Name"
									columnKey="name"
									key={"phase"}
								/>
							),
							dataIndex: "name",
							key: "name",
							render: (value: any, record: any, index: any) => {
								let fileType: string = record.file
									? "file"
									: record.folder
										? "folder"
										: "";

								let prefix = record.name;
								const lastDotIndex = record.name?.lastIndexOf(".");

								if (lastDotIndex != -1) {
									prefix = record.name.substring(0, lastDotIndex);
								}
								let checked = rowSelectedIDs.includes(record.id);

								return (
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<div
											style={{
												display: "flex",
												flexDirection: "row",
												alignItems: "center",
												gap: "10px",
											}}
										>
											<Checkbox
												checked={rowSelectedIDs.includes(record.id)}
												key={`${record.id}-checkbox`}
												onChange={(e: CheckboxChangeEvent) => {
													let tempIds = [...rowSelectedIDs];
													if (e.target.checked) {
														tempIds.push(record.id);
													} else {
														const foundIndex = rowSelectedIDs.findIndex(
															(id: string) => record.id === id
														);
														if (foundIndex != -1) {
															tempIds.splice(foundIndex, 1);
														}
													}
													console.log(record.id);
													console.log(tempIds);
													setRowSelectedIDs(tempIds);
												}}
											></Checkbox>
											<a
												className="table-clickable-text flex align-items-center gap-2"
												onClick={() => {
													if (record.folder) {
														handleFolderChange(record.id, record.name);
													} else if (record.file) {
														if (
															can("collab") &&
															fileTools.checkSharepointEdittable(
																record.file.mimeType
															)
														) {
															resolvedApi
																.collaborate(record.id, containerId)
																.then((response: any) => {
																	window.open(response?.link.webUrl, "__blank");
																});
														} else if (can("preview")) {
															window.open(
																resolvedApi.previewPath(
																	record.id,
																	containerId,
																	location.pathname
																),
																"_blank"
															);
														}
													}
												}}
											>
												<span className="table-clickable-icon-text">
													{getFileIcon(record.name, !!record.folder)}{" "}
												</span>
												{prefix}
											</a>
										</div>

										<Dropdown
											disabled={sharepointDisabled}
											menu={{
												items: dropdownItemOptions(fileType, record, index),
											}}
											trigger={["click"]}
										>
											<a onClick={(e) => e.preventDefault()}>
												{GetAntIcon("ellipsis")}
											</a>
										</Dropdown>
									</div>
								);
							},
						},
						{
							title: (
								<SearchButton
									handleSearch={handleSearch}
									headerName="Owner"
									columnKey="owner"
									key={"owner"}
								/>
							),
							dataIndex: "owner",
							key: "owner",
							render: (value: any, record: any) => {
								let owner = ownerList.find(
									(owner: any) => record.id === owner.item_id
								);
								return owner?.owner_email || "-";
							},
							// width: 200,
						},
						{
							title: "Date added",
							dataIndex: "createdDateTime",
							key: "creation_date",
							render: (value: any) => (
								<span>
									{value && dayjs(value)?.format("D MMM YYYY HH:mm:ss")}
								</span>
							),
							sorter: (a: any, b: any) => {
								const stringA = a.createdDateTime || "";
								const stringB = b.createdDateTime || "";
								return stringA.localeCompare(stringB);
							},
						},
						{
							title: "Last modified",
							dataIndex: "lastModifiedDateTime",
							key: "last_modified_date",
							render: (value: any) => (
								<span>
									{value && dayjs(value)?.format("D MMM YYYY HH:mm:ss")}
								</span>
							),
							sorter: (a: any, b: any) => {
								const stringA = a.lastModifiedDateTime || "";
								const stringB = b.lastModifiedDateTime || "";
								return stringA.localeCompare(stringB);
							},
						},
						{
							title: "Type",
							dataIndex: "type",
							key: "type",
							filters: filterFileTypeOptions(),
							filterDropdown: renderTableCheckboxFilter,
							onFilter: (value: any, record: any) => {
								console.log(record.file?.mimeType, value);
								return (
									record.file?.mimeType == value ||
									(record.folder && value === "File folder")
								);
							},
							render: (value: any, record: any) => {
								if (record.folder) {
									return <span>File folder</span>;
								} else if (record.file) {
									return (
										<span>
											{fileTools.getReadableFileType(record.file.mimeType)}
										</span>
									);
								}
							},
						},
						{
							title: "Size",
							dataIndex: "size",
							key: "size",
							sorter: (a: any, b: any) => a.size - b.size,
							render: (value: any) => {
								// let renderedValue = (value / 1000000).toFixed(4);
								// return <span>{value && renderedValue}</span>;

								return <span>{fileTools.formatFileSize(value)}</span>;
							},
						},
					]}
					rowClassName={(record: any) => {
						console.log(hovered?.id, record.id);
						return hovered?.id === record.id ? "hovered-row" : "";
					}}
					onRow={(_record: any) => ({
						draggable: can("move"),
						onDragStart: (event) => {
							setHovered(null);
							setDraggingRecord(_record);
							event.dataTransfer.setData("recordId", _record?.id);
							event.dataTransfer.effectAllowed = "move";
						},
						onDragOver: (event: any) => {
							setHovered(_record);
							event.preventDefault();
							event.stopPropagation();
							if (_record.folder) {
								// add hover class to current row td

								document
									.querySelector(".document-container-drop-area-content")
									?.classList.remove(
										"document-container-drop-area-content-hover"
									);
							} else if (!draggingRecord) {
								document
									.querySelector(".document-container-drop-area-content")
									?.classList.add("document-container-drop-area-content-hover");
							}
						},
						onDragLeave: (event: any) => {
							setHovered(null);
							event.preventDefault();
							event.stopPropagation();
						},
						onDrop: (event: any) => {
							setHovered(null);
							event.preventDefault();
							event.stopPropagation();
							// what about outside the table?
							setDraggingRecord(null);
							const recordId = event.dataTransfer.getData("recordId");

							if (draggingRecord) {
								if (
									!can("move") ||
									_record?.id === recordId ||
									!_record.folder
								) {
									return;
								}
								handleFileMove(_record?.id, recordId);
							} else if (can("upload") && event.dataTransfer.files.length > 0) {
								document
									.querySelector(".document-container-drop-area-content")
									?.classList.remove(
										"document-container-drop-area-content-hover"
									);
								let files = Array.from(event.dataTransfer.files);
								if (_record.folder) {
									handleFileUpload(files, _record?.id);
								} else {
									handleFileUpload(files);
								}
							}
						},
						onDragEnd: (event: any) => {
							console.log("drag end");
							setHovered(null);
							setDraggingRecord(null);
						},
					})}
				></Table>
			</>
		);
	}, [
		currentFolderId,
		filteredItems,
		currentFolderFiles,
		folderHierarchy,
		rowSelectedIDs,
		fileList,
		ownerList,
		draggingRecord,
		hovered,
		containerId,
		resolvedApi,
		allowedActions,
	]);
	const createFolderModal = () => {
		return (
			<Modal
				closable={false}
				width={"40%"}
				centered
				styles={{ header: { padding: "12px 16px" } }}
				open={folderModalOpen}
				title="Create new folder"
				footer={() => (
					<div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
						<Button
							onClick={() => {
								setFolderName("");
								setFolderModalOpen(false);
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							disabled={folderName == ""}
							onClick={handleFolderCreation}
						>
							Save
						</Button>
					</div>
				)}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
					<span>Folder name</span>
					<Input
						value={folderName}
						onChange={(event: any) => {
							console.log(event.target.value);
							setFolderName(event.target.value);
						}}
						allowClear
					></Input>
				</div>
			</Modal>
		);
	};

	const renameFileModal = () => {
		let filetype = tempRecord.file ? "file" : "folder";
		return (
			<Modal
				closable={false}
				width={"40%"}
				centered
				styles={{ header: { padding: "12px 16px" } }}
				open={renameModalOpen}
				title={`Rename ${filetype}`}
				footer={() => (
					<div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
						<Button
							onClick={() => {
								setTempFileName("");
								setNewFileName("");
								setTempRecord({});
								setRenameModalOpen(false);
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							disabled={tempFileName == newFileName}
							onClick={handleFileRename}
						>
							Save
						</Button>
					</div>
				)}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
					<span>New {filetype} name</span>
					<Input
						value={newFileName}
						onChange={(event: any) => {
							setNewFileName(event.target.value);
						}}
						allowClear
					></Input>
				</div>
			</Modal>
		);
	};

	const deleteFileModal = () => {
		let recordIdList = isDeleteMultiple ? rowSelectedIDs : [tempRecord.id];

		let filetype = tempRecord.file ? "file" : "folder";

		return (
			<Modal
				closable={false}
				width={"40%"}
				centered
				styles={{ header: { padding: "12px 16px" } }}
				open={deleteModalOpen}
				title={`Delete ${isDeleteMultiple ? "file(s)" : filetype}`}
				footer={() => (
					<div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
						<Button
							onClick={() => {
								setDeleteModalOpen(false);
								setTempRecord({});
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							danger
							onClick={() =>
								handleFileDelete(
									recordIdList,
									isDeleteMultiple,
									tempRecord,
									filetype,
									tempRecord?.index
								)
							}
						>
							Delete
						</Button>
					</div>
				)}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
					<span>
						Are you sure you want to permanently delete{" "}
						{isDeleteMultiple ? "these files" : `this ${filetype}`}? Once
						deleted, it cannot be restored.
					</span>
				</div>
			</Modal>
		);
	};

	const confirmUploadModal = () => {
		let recordIdList = isDeleteMultiple ? rowSelectedIDs : [tempRecord.id];

		let filetype = tempRecord.file ? "file" : "folder";

		return (
			<Modal
				closable={false}
				width={"40%"}
				centered
				styles={{ header: { padding: "12px 16px" } }}
				open={confirmUploadModalOpen}
				title={`Upload confirmation`}
				footer={() => (
					<div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
						<Button
							onClick={() => {
								setFileList([]);
								setUploadLength(0);
								setConfirmUploadModalOpen(false);
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							danger
							onClick={() => {
								setConfirmUploadModalOpen(false);
								handleFileUpload(fileList);
							}}
						>
							Proceed
						</Button>
					</div>
				)}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
					<span>
						A file with the same name has already been uploaded. Uploading it
						again will replace the existing file. Do you wish to proceed?
					</span>
				</div>
			</Modal>
		);
	};

	const loadingModal = () => {
		let loadingTime = 0;

		// start a timer when the modal is opened
		useEffect(() => {
			const timer = setInterval(() => {
				loadingTime++;
			}, 1000);
			return () => clearInterval(timer);
		}, []);

		let filetype = tempRecord.file ? "file" : "folder";
		let modalTitle = "";
		if (actionType === "upload") {
			modalTitle = "File upload";
		} else if (actionType === "delete") {
			modalTitle = `Delete ${isDeleteMultiple ? "file(s)" : filetype}`;
		} else if (actionType === "move") {
			modalTitle = "Move file(s)";
		}

		return (
			<Modal
				closable={false}
				width={"40%"}
				centered
				title={modalTitle}
				styles={{
					header: { padding: "12px 30px" },
					body: {
						padding: "30px",
						minHeight: "350px",
						height: "350px",
						width: "100%",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					},
				}}
				open={loadingModalOpen}
				footer={null}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						top: 0,
						insetInlineStart: 0,
						height: "80%",
						width: "100%",
						margin: 0,
					}}
				>
					<img
						className="no-visual-search"
						style={{ position: "absolute", height: "40%" }}
						src={elementalLoadingLogo}
					/>
					<img
						className="spin no-visual-search"
						style={{ position: "absolute", height: "55%" }}
						src={elementalLoadingOutline}
					/>
				</div>
				<div
					style={{
						width: "100%",
						display: "flex",
						flexDirection: "column",
						gap: "10px",
					}}
				>
					<span>
						{actionType === "upload"
							? "Uploading"
							: actionType === "delete"
								? "Deleting"
								: "Moving"}{" "}
						files...
						{/* if took more than 10 seconds, show a message to the user */}
						{loadingTime && actionType === "upload" && loadingTime > 10 && (
							<span>
								The upload is taking longer than expected. Please wait.
							</span>
						)}
					</span>
					<Progress percent={loadingPercentage}></Progress>
				</div>
			</Modal>
		);
	};

	return (
		<>
			{visible && (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						flex: 1,
						width: "100%",
						padding: "40px 30px",
					}}
				>
					<div
						id="quick-access-container"
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "20px",
						}}
					>
						{/* <div
							style={{
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
								gap: "6px",
							}}
						>
							{" "}
							<span style={{ fontSize: "16px" }}>Quick Access</span>
							<Tooltip title="Showing recently added files">
								<span style={{ opacity: "0.5" }}>
									{GetAntIcon("question3")}
								</span>
							</Tooltip>
							<span
								onClick={() => {
									setQuickAccessCollapsed(!quickAccessCollapsed);
								}}
							>
								{quickAccessCollapsed
									? GetAntIcon("downarrow")
									: GetAntIcon("up")}
							</span>
						</div> */}

						<SharepointQuickAccess
							api={resolvedApi}
							canCollaborate={can("collab")}
							files={quickAccessFiles}
							fileMetaData={quickAccessFileMetaData}
							ownerList={ownerList}
							containerId={containerId}
							tenantKey={context.currentTenantKey}
							collapsed={quickAccessCollapsed}
							onToggleCollapse={() =>
								setQuickAccessCollapsed(!quickAccessCollapsed)
							}
						/>
					</div>
					{/* allow this component to be able to drop files from windows explorer */}
					<div
						id="documentContainer"
						style={{
							gap: "20px",
							display: "flex",
							flexDirection: "column",
							flex: 1,
							position: "relative",
							zIndex: 1000,
						}}
						onDrop={(event: any) => {
							event.preventDefault();
							const files = event.dataTransfer.files;
							console.log(files);

							if (can("upload") && files.length > 0) {
								handleFileUpload(Array.from(files));
							}

							document
								.querySelector(".document-container-drop-area-content")
								?.classList.remove(
									"document-container-drop-area-content-hover"
								);
						}}
						onDragOver={(event: any) => {
							event.preventDefault();

							if (draggingRecord || !can("upload")) {
								return;
							}
							document
								.querySelector(".document-container-drop-area-content")
								?.classList.add("document-container-drop-area-content-hover");
						}}
						onDragLeave={(event: any) => {
							event.preventDefault();
							if (draggingRecord) {
								return;
							}

							// Only remove hover if we're actually leaving the container
							// (not just moving between child elements)
							const currentTarget = event.currentTarget;
							const relatedTarget = event.relatedTarget as Element;

							// Check if relatedTarget is still within our container
							if (relatedTarget && currentTarget.contains(relatedTarget)) {
								return; // Still inside, don't remove hover
							}
							document
								.querySelector(".document-container-drop-area-content")
								?.classList.remove(
									"document-container-drop-area-content-hover"
								);
						}}
					>
						{/* create an invisible div with the same size as the document container */}
						<div
							className="document-container-drop-area-content"
							style={{
								position: "absolute",
								flex: 1,
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								gap: "8px",
								width: "100%",
								height: "100%",
								pointerEvents: "none",
							}}
						>
							{/* <div
								className="document-container-drop-area-content-text"
								style={{ pointerEvents: "none" }}
							>
								<span style={{ fontSize: "64px", color: "#ffffff" }}>
									{GetAntIcon("upload")}
								</span>
								<span
									style={{
										fontSize: "24px",
										fontWeight: "bold",
										color: "#ffffff",
									}}
								>
									Drop files here to upload
								</span>
								<span style={{ color: "#ffffff" }}>
									Files will be uploaded to the current folder
								</span>
							</div> */}
						</div>
						{folderMemo}
					</div>
				</div>
			)}
			{createFolderModal()}
			{confirmUploadModal()}
			{renameFileModal()}
			{deleteFileModal()}
			{loadingModal()}
		</>
	);
};

export default SharepointComponent;
